import * as React from "react";
import {
    withRouter,
    useLocation,
    Redirect,
    RouteComponentProps,
} from "react-router-dom";
import { useSelector } from "react-redux";
import NProgress from "nprogress";

import API from "../api/user";
import Config from "../constants/Config";
import getContentReferenceTileProps from "../utils/getContentReferenceTileProps";
import useAsyncState from "../hooks/useAsyncState";
import Loader from "../components/atoms/Loader";
import HeroProfilePage from "../components/organisms/HeroProfilePage";
import CustomMeta from "../components/atoms/CustomMeta.js";
import NavManageEvent from "../components/organisms/NavManageEvent";
import ManageEventAttendees from "../components/organisms/ManageEventAttendees";
import TicketDetailManageModal from "../components/organisms/TicketDetailManageModal";
import QrScannerModal from "../components/organisms/QrScannerModal";
import Tile from "../components/molecules/Tile";
import NotFound from "./NotFound";
import ManualAttendanceEntryForm, {
    AttendanceData,
} from "../components/organisms/ManualAttendanceEntryForm";
import { isBefore, isWithinInterval, parseISO } from "date-fns";
import { parseDateString } from "../utils/date";

interface PropsType extends RouteComponentProps {
    match: any;
}

const ManageEvent = (props: PropsType) => {
    const attendeePollingLength = 60000;
    const [state, localActions] = useAsyncState();
    const location = useLocation();
    const [pageError, setPageError] = React.useState<any>(null);
    const [activeEvent, setActiveEvent] = React.useState<any>(null);
    const [eventAttendance, setEventAttendance] = React.useState<any>(null);
    const [eventStatus, setEventStatus] = React.useState<any>(null);
    const [eventAttendees, setEventAttendees] = React.useState<any>(null);
    const [activeAttendee, setActiveAttendee] = React.useState<any>(null);
    const [qrScannerActive, setQrScannerActive] = React.useState<any>(false);
    const [qrScannerMode, setQrScannerMode] = React.useState<any>(false);
    const [qrScannerError, setQrScanError] = React.useState<any>(null);
    const attendeesPollingInterval = React.useRef<ReturnType<
        typeof setInterval
    > | null>(null);

    const { profile } = useSelector((state: any): any => state.user);
    const isClubExec = profile.role
        ? profile.role.filter(role => role === "club_exec").length > 0
        : false;
    const isManagedEvent =
        location && location.pathname.indexOf(Config.routes.managedEvent) >= 0;
    const canManageEvents =
        profile.permissions &&
        profile.permissions.includes("auts_event_checkin_access");

    const attendanceData =
        eventAttendance?.reported_attendance_figure != null &&
        eventAttendance?.reported_student_attendance != null
            ? {
                  totalAttendees: eventAttendance.reported_attendance_figure,
                  totalStudentAttendees:
                      eventAttendance.reported_student_attendance,
              }
            : undefined;

    const submitAttendanceData = async ({
        totalAttendees: reported_attendance_figure,
        totalStudentAttendees: reported_student_attendance,
    }: AttendanceData) => {
        await API.userUpdateManagedEventAttendees(props.match.params.id, {
            reported_attendance_figure,
            reported_student_attendance,
        });
        await API.userGetManagedEvent(props.match.params.id).then(
            (data: any) => {
                if (data.event) {
                    setActiveEvent(data.event);
                }
                if (data.event_attendance) {
                    setEventAttendance(data.event_attendance);
                }

                setEventStatus(data.publish_status);
            }
        );
    };
    const noExistingCheckin =
        eventAttendees?.length > 0
            ? eventAttendees.every?.(attendee => {
                  return !attendee.is_checkin;
              })
            : true;
    const showManualAttendanceEntryForm =
        eventAttendance?.is_submitted ||
        (noExistingCheckin &&
            activeEvent?.acf?.end_date &&
            (eventAttendance?.submission_until
                ? isBefore(
                      parseDateString(activeEvent?.acf?.end_date),
                      parseISO(eventAttendance.submission_until)
                  ) &&
                  isWithinInterval(new Date(), {
                      start: parseDateString(activeEvent?.acf?.end_date),
                      end: parseISO(eventAttendance.submission_until),
                  })
                : true));

    console.log({
        noExistingCheckin,
        eventAttendees,
        is_submitted: eventAttendance?.is_submitted,
        end_date: activeEvent?.acf?.end_date,
        submission_until: eventAttendance?.submission_until,
    });

    // const { previousPage } = useSelector((state: any): any => state.ui);

    const mappedHeroTileProps = activeEvent
        ? getContentReferenceTileProps(activeEvent, "club-event-detail")
        : {};

    // initial mount use effect
    React.useEffect((): any => {
        if (!isClubExec && !canManageEvents) {
            <Redirect to={Config.routes.login} />;
        }

        if (props.match.params.id) {
            NProgress.start();
            localActions.request();
            setPageError(null);

            Promise.all([
                // event detail
                API.userGetManagedEvent(props.match.params.id).then(
                    (data: any) => {
                        if (data.event) {
                            setActiveEvent(data.event);
                        }
                        if (data.event_attendance) {
                            setEventAttendance(data.event_attendance);
                        }

                        setEventStatus(data.publish_status);
                    }
                ),
                // event attendees list
                getAttendeesList(),
            ])
                .then(() => {
                    // success
                    localActions.success();

                    // start attendee list update polling
                    startAttendeeListPolling();
                })
                .catch(error => {
                    // errors
                    // localActions.fail(error.message);
                    setPageError(error);
                })
                .finally(() => {
                    NProgress.done();
                });
        } else {
            setPageError({});
            // localActions.fail("Could not find a matching managed event.");
        }

        return () => {
            if (attendeesPollingInterval && attendeesPollingInterval.current) {
                clearInterval(attendeesPollingInterval.current);
            }
        };
    }, []); // eslint-disable-line

    // qr scanner
    React.useEffect((): any => {
        if (!qrScannerActive) {
            // clear previous qr scan error
            setQrScanError(null);
        }
    }, [qrScannerActive]);

    // new attendees list
    React.useEffect((): any => {
        if (activeAttendee) {
            const matchingQrAttendee = eventAttendees.find(attendee => {
                return (
                    attendee.event_registration_id ===
                        activeAttendee.event_registration_id ||
                    attendee.name === activeAttendee.name
                );
            });

            if (matchingQrAttendee) {
                setActiveAttendee(matchingQrAttendee);
            }
        }
    }, [activeAttendee, eventAttendees]);

    const getBackLink = () => {
        // const splitPrevious = previousPage ? previousPage.split("/") : null;

        // const backLinkTo =
        //     splitPrevious[2] &&
        //     splitPrevious[2] === "manage-clubs" &&
        //     splitPrevious[3]
        //         ? previousPage
        //         : Config.routes.manageClubs;

        let backLinkTo = "";

        if (!isClubExec) {
            backLinkTo = Config.routes.managedEvents;
        } else if (isManagedEvent) {
            backLinkTo = Config.routes.managedEvents;
        } else {
            if (window.club_exec_club_id) {
                backLinkTo = `${Config.routes.manageClubs}/${window.club_exec_club_id}`;
            } else {
                backLinkTo = Config.routes.manageClubs;
            }
        }

        return backLinkTo;
    };

    const startAttendeeListPolling = () => {
        attendeesPollingInterval.current = setInterval(() => {
            getAttendeesList();
        }, attendeePollingLength);
    };

    const getAttendeesList = (attendeeQrCodeName?: any) => {
        return API.userGetManagedEventAttendees(props.match.params.id).then(
            (data: any) => {
                setEventAttendees(
                    data.map(listAttendee => {
                        if (listAttendee.name === attendeeQrCodeName) {
                            listAttendee.is_checkin = true;
                        }
                        return listAttendee;
                    })
                );
            }
        );
    };

    const handleQrScanSuccess = qrScanResult => {
        if (qrScanResult && qrScanResult.data) {
            // attempt check in with result
            // if successful,if user not in attendee list fetch new attendee list, then show ticketdetalmanagemodal success for user
            // if fail, show back end error in qr scan modal

            // hide scanner modal
            setQrScannerActive(false);

            // set active attendee (just with qr scan result) to trigger ticket modal with qr scan context
            setQrScannerMode(true);
            setActiveAttendee({ name: qrScanResult.data });

            // OLD
            // find matching attendee from list by 'name' property and qr data

            // const matchingQrAttendee = eventAttendees.find(attendee => {
            //     return attendee.name === qrScanResult.data;
            // });

            // if (matchingQrAttendee && matchingQrAttendee.is_checkin) {
            //     // ticket is in event list but is already checked
            //     setQrScanError("This ticket has already been checked in.");
            // } else if (matchingQrAttendee) {
            //     // ticket is in event list, check user in

            //     // set active attendee/modal with qr scan context
            //     setQrScannerMode(true);
            //     setActiveAttendee(matchingQrAttendee);

            //     // hide scanner modal
            //     setQrScannerActive(false);
            // } else {
            //     // no matching ticket found
            //     setQrScanError(
            //         "Invalid QR code or was unable to find matching ticket for this event."
            //     );
            // }
        }
    };

    const handleOnCheckInSuccess = (wasQrScanned, qrCodeName) => {
        const matchingQrAttendee = eventAttendees.find(attendee => {
            return attendee.name === qrCodeName;
        });

        if (wasQrScanned && !matchingQrAttendee) {
            // check in was a qr scan, and check in user was not already in list, fetch new list
            getAttendeesList(qrCodeName);
        } else {
            // update check in status of user in already local state attendee list
            setEventAttendees(
                eventAttendees.map(listAttendee => {
                    if (listAttendee.name === activeAttendee.name) {
                        listAttendee.is_checkin = true;
                    }
                    return listAttendee;
                })
            );
        }

        // get and set updated attendee list - commented out as api does not have updated results
        // localActions.request();
        // getAttendeesList()
        //     .then(() => {
        //         localActions.success();
        //     })
        //     .catch(error => {
        //         localActions.fail(error.message);
        //     });
    };

    const handleAttendeeClick = attendee => {
        setQrScannerMode(false);
        setActiveAttendee(attendee);
    };

    const handleLaunchQrScanner = () => {
        setQrScanError(null);
        setQrScannerActive(true);
    };

    if (pageError) {
        return <NotFound {...pageError} />;
    }

    return (
        <React.Fragment>
            <CustomMeta
                title={`Manage Event ${
                    activeEvent ? ` - ${activeEvent.post_title}` : ""
                }`}
            />

            <HeroProfilePage
                backLink={{
                    title: "Back to list",
                    url: getBackLink(),
                }}
                customClass="manage-clubs"
            >
                <Tile
                    {...mappedHeroTileProps}
                    expanded={true}
                    hero={true}
                    // status={eventStatus}
                />
            </HeroProfilePage>

            <div className="container-fluid">
                <div className="row">
                    <div className="col-xs-12 col-md-offset-1 col-md-10">
                        <div className="row manage-clubs__content">
                            <div className="col-xs-12">
                                <Loader active={state.loading}>
                                    {activeEvent ? (
                                        <NavManageEvent
                                            attendees={eventAttendees}
                                            event={activeEvent}
                                            eventStatus={eventStatus}
                                            toggleModalState={
                                                handleLaunchQrScanner
                                            }
                                            isClubExec={
                                                isClubExec && !isManagedEvent
                                            }
                                            canManageEvents={canManageEvents}
                                            scanTicketDisabled={
                                                eventAttendance?.is_submitted
                                            }
                                        />
                                    ) : null}

                                    {showManualAttendanceEntryForm ? (
                                        <ManualAttendanceEntryForm
                                            attendanceData={attendanceData}
                                            onSubmit={submitAttendanceData}
                                        />
                                    ) : null}

                                    {activeEvent && eventAttendees ? (
                                        <ManageEventAttendees
                                            id={props.match.params.id}
                                            attendees={eventAttendees}
                                            onAttendeeClick={
                                                handleAttendeeClick
                                            }
                                        />
                                    ) : null}
                                </Loader>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <TicketDetailManageModal
                active={activeAttendee ? true : false}
                event={activeEvent}
                attendee={activeAttendee}
                qrScanned={qrScannerMode}
                onScanAnotherQrCode={() => setQrScannerActive(true)}
                onCheckInSuccess={handleOnCheckInSuccess}
                onClose={() => setActiveAttendee(false)}
                checkinDisabled={eventAttendance?.is_submitted}
            />

            <QrScannerModal
                active={qrScannerActive}
                error={qrScannerError}
                onScanned={handleQrScanSuccess}
                onClose={() => setQrScannerActive(false)}
            />
        </React.Fragment>
    );
};

ManageEvent.preload = ({ location, passed: { store } }: any): Promise<any> => {
    return Promise.all([]);
};

export default withRouter(ManageEvent);
